// Shared between the Usage page (full daily-cost chart + per-agent breakdown)
// and the Home overview (smaller cost-trend card). The functions here are
// pure date math + bucket lookup — no React, no API, no DOM. Extracted so
// both consumers operate on the same canonical "fill empty days with zero"
// rule rather than each page reinventing it.

import type { UsageBucket } from '@cortex/shared';
import { formatShortDay } from './format-date.js';

export interface ChartPoint {
  bucket: string;
  costUsd: number;
}

// Backfill missing days as zero-cost points so the line is continuous and
// "quiet day" reads as a flat segment rather than a misleading interpolation.
export function backfillSeries(
  series: UsageBucket[],
  periodStart: string,
  periodEnd: string,
): ChartPoint[] {
  const byBucket = new Map(series.map((b) => [b.bucket, b]));
  const start = utcDay(new Date(periodStart));
  const end = utcDay(new Date(periodEnd));
  const today = utcDay(new Date());
  // Only chart up to today (inclusive) to avoid a long tail of empty days
  // out to the end of the Stripe window.
  const last = today.getTime() < end.getTime() ? today : end;
  const out: ChartPoint[] = [];
  for (let t = start.getTime(); t <= last.getTime(); t += 86_400_000) {
    const key = isoDay(new Date(t));
    const row = byBucket.get(key);
    out.push({ bucket: key, costUsd: row ? row.costUsd : 0 });
  }
  return out;
}

export function utcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// Format an ISO day key (YYYY-MM-DD) as a short chart-axis label
// (`23/04` in pt-BR). Pinned via formatShortDay so chart tick labels
// stay consistent with the rest of the dashboard regardless of viewer
// locale.
export function shortDay(iso: string): string {
  return formatShortDay(`${iso}T00:00:00Z`);
}

// Days remaining until `end`, floored at 0. Used by widgets that show
// "resets in N days" alongside a usage gauge. Computed from the current
// wall clock — caller passes the period end ISO string from /v1/usage.
export function daysUntil(endIso: string): number {
  const end = new Date(endIso).getTime();
  const now = Date.now();
  if (end <= now) return 0;
  return Math.ceil((end - now) / 86_400_000);
}
